import I18n from 'I18n';
import { CANDY_APPLE_DARK, CHART_GREEN, CHART_RED, OZ_DARK } from 'HubStyleTokens/colors';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import { FRAMEWORK_PROPERTY_TO_INSIGHT_PROPERTY, FRAMEWORK_INSIGHT_POLARITY } from 'messaging-publishing-lib/Insights/constant';
import PortalIdParser from 'PortalIdParser';
import { isSupportedFrameworkInsight, isSupportedFrameworkInsightContext } from './support';
export const isPostSendPage = /^\/email\/[^/]+\/details\/[^/]+\/performance$/.test(window.location.pathname);
export const isRecipientsPage = /^\/email\/[^/]+\/details\/[^/]+\/recipients\/[^/]+$/.test(window.location.pathname);
const getInsightProperty = propertyName => FRAMEWORK_PROPERTY_TO_INSIGHT_PROPERTY[propertyName];
export function getInsightPolarityForMetricState(metricState) {
  if (metricState === 'GOOD') return true;
  if (metricState === 'BAD') return false;
  return null;
}
export const isParsedInsightGood = parsedInsight => parsedInsight.polarity === FRAMEWORK_INSIGHT_POLARITY.GOOD;
export const unparseInsight = parsedInsight => {
  return Object.values(parsedInsight).join('__');
};
export const parseInsight = insight => {
  const [context, property, polarity] = insight.split('__');
  return {
    context,
    property,
    polarity
  };
};
export const getInsights = crmObject => {
  if (!crmObject) return [];
  const rawPropertyValue = getPropertyValue(crmObject, 'hs_metric_insights');
  const frameworkInsights = (rawPropertyValue === null || rawPropertyValue === void 0 ? void 0 : rawPropertyValue.split(';')) || [];
  const supportedInsights = frameworkInsights.filter(isSupportedFrameworkInsight);
  return supportedInsights;
};
export const getInsightColor = polarity => polarity ? OZ_DARK : CANDY_APPLE_DARK;
export const getInsightColorTrellis = polarity => polarity ? 'do-not-use-oz-dark' : 'do-not-use-candy-apple-dark';
export const getInsightChartColorTrellis = polarity => polarity ? 'do-not-use-chart-green' : 'do-not-use-chart-red';
export const getInsightChartColor = polarity => polarity ? CHART_GREEN : CHART_RED;

/**
 * Gets the polarity of an entire crmObject or
 * individual propertyName (if provided).
 *
 * @returns the polarity as a boolean or null if there is no insight.
 */
export const getInsightPolarity = ({
  crmObject,
  propertyName,
  context = 'PORTAL_CONTEXT'
}) => {
  const insights = getInsights(crmObject);
  const parsedInsights = insights.map(parseInsight);
  const parsedInsightsInContext = parsedInsights.filter(({
    context: possibleContext
  }) => possibleContext === context);
  if (propertyName) {
    const property = getInsightProperty(propertyName);
    const possibleParsedInsightInContext = parsedInsightsInContext.find(({
      property: possibleProperty
    }) => possibleProperty === property);
    return possibleParsedInsightInContext ? isParsedInsightGood(possibleParsedInsightInContext) : null;
  }
  return parsedInsightsInContext.length ? parsedInsightsInContext.every(isParsedInsightGood) : null;
};
export const getEmailDetilsPageLink = emailId => `/email/${PortalIdParser.get()}/details/${emailId}/performance`;
export const formatRate = rate => I18n.formatPercentage(rate, {
  precision: 1
});
export const formatCount = count => I18n.formatNumber(count, {
  precision: 1
});
export const absoluteDifference = (num1, num2) => {
  return Math.abs(num1 - num2);
};

/**
 * @param businessUnit could be the special string
 * `ALL`. Otherwise, it should be the businessUnitId.
 */
export const getShouldUseBusinessUnitContext = businessUnit => {
  return Number(businessUnit) > 0;
};
export function getBenchmarkRate(benchmarkStats) {
  if (!benchmarkStats) return 0;
  const benchmarkRate = benchmarkStats.rateToBenchmarkComparison === 'BAD' ? benchmarkStats.benchmarkRates.badRate : benchmarkStats.benchmarkRates.goodRate;
  return benchmarkRate;
}
export function getBenchmarkStatsForType(benchmarkType, metadata) {
  return metadata.benchmarkStats.find(benchmark => benchmark.benchmarkType === benchmarkType);
}
export function getBenchmarkDifference(benchmarkStats, metadata) {
  return absoluteDifference(metadata.metricRate, getBenchmarkRate(benchmarkStats));
}
export function getBenchmarkKey(benchmarkStats, metadata) {
  const isBadOrOkay = (benchmarkStats === null || benchmarkStats === void 0 ? void 0 : benchmarkStats.rateToBenchmarkComparison) === 'OKAY' || (benchmarkStats === null || benchmarkStats === void 0 ? void 0 : benchmarkStats.rateToBenchmarkComparison) === 'BAD';
  const goodOrBadRateToBenchmarkComparison = isBadOrOkay ? 'BAD' // OKAY is BAD compared against the GOOD rate
  : 'GOOD';
  return `${goodOrBadRateToBenchmarkComparison}_${metadata.metricType}`;
}
export function getAvailableContexts({
  shouldUseBusinessUnitContext,
  hasCampaign,
  isAutomatedEmail,
  industryName,
  isUngatedForGARelease,
  isUngatedForBenchmarkInsight
}) {
  const availableContexts = [];
  if (isUngatedForBenchmarkInsight) {
    availableContexts.push('GLOBAL_BENCHMARK_CONTEXT');
  }
  if (industryName !== undefined && isUngatedForGARelease) {
    availableContexts.push('INDUSTRY_BENCHMARK_CONTEXT');
  }
  availableContexts.push(shouldUseBusinessUnitContext ? 'BUSINESS_UNIT_CONTEXT' : 'PORTAL_CONTEXT');
  if (hasCampaign !== false) {
    availableContexts.push('PRODUCT_CAMPAIGN_CONTEXT');
  }
  if (isAutomatedEmail !== false) {
    availableContexts.push('SAME_EMAIL_MONTH_OVER_MONTH_CONTEXT');
  }
  return availableContexts.filter(isSupportedFrameworkInsightContext);
}
export function getDefaultContext({
  shouldUseBusinessUnitContext,
  hasCampaign,
  isAutomatedEmail
}) {
  if (isAutomatedEmail) {
    return 'SAME_EMAIL_MONTH_OVER_MONTH_CONTEXT';
  }
  if (hasCampaign) {
    return 'PRODUCT_CAMPAIGN_CONTEXT';
  }
  return shouldUseBusinessUnitContext ? 'BUSINESS_UNIT_CONTEXT' : 'PORTAL_CONTEXT';
}